<template lang="pug">
#other-user-template-input
  // title
  .d-flex.flex-row.justify-content-center.mb-3
    .page-title 安否確認（施設関係者）の事前設定

  // progress
  ProcessIndicator(v-model="indicator")
  .mb-3.separator

  // discription
  .mb-3
    .discription 宛先・メッセージ内容・質問項目を事前に設定することができます（質問内容は事前設定が必須です）。宛先用の宛先グループは、パソコンもしくはタブレットの管理システムで作成してください。

  // safetyConfirm message content
  .mb-1
    span.key-name メッセージの内容
  // targets
  .mb-3
    .d-flex.flex-row.align-items-top.mb-2
      .target-add-btn.mr-2
        b-button.btn(@click="showTargetSelectModal") 宛先選択
    .d-flex.flex-row.align-items-top
      .target-names
        // send group targets
        .send-group-targets.d-flex.flex-row.flex-wrap
          template(v-for="target in groupTargets")
            template(v-for="group in target.targets")
              button.target-name(v-show="group.isTarget == true" @click="onSendTarget(group)") {{ group.name }}

  // text
  .mb-3
    .d-flex.flex-row.align-items-top
      .flex-grow-1.mr-2
        b-form-textarea#text(v-model="openingSentence" name="text"
        placeholder="施設関係者に送るメッセージ内容を入力してください。\n\n例：\n安否確認のテストを実施します。\nみなさんの仮の安否情報を入力してください。"
        rows="7" max-rows="7" maxlength="300" @focus="hideFooter" @blur="showFooter" v-validate="{ max: 300 }"
        aria-describedby="text-live-feedback" data-vv-as="メッセージ内容")
    .error-messages.mt-1(v-if="errors.has('text')")
      label.error-message(v-for="error in errors.collect('text')") {{ error }}

  // questions content
  SafetyConfirmOtherUserQuestions(v-model="confirms")

  // buttons
  .mb-3
    ValidationAlert(v-model="showValidationAlert")
  .d-flex.flex-row.justify-content-center
    b-button.btn(@click="gotoConfirm") 確認画面へ

  // loading modal
  Loading(:isBusy="isBusy" :message="loadingMessage")
  // send target modals
  SafetyConfirmTargetMessageGroupModal(v-model="groupTargets" :selectedGroupType.sync="selectedGroupType" :show.sync="showGroupTargetModal")
  // send target delete modal
  OkCancelModal(v-model="showSendTargetDeleteModal" :title="sendTargetDeleteMessage" @ok="onSendTargetDeleteOk")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { userStore, mobileSafetyConfirmOtherUserTemplateStore } from "@/store/store";
import { MobileSafetyConfirmOtherUserTemplateState } from "@/store/MobileSafetyConfirmOtherUserTemplate";
import { MobileUrl } from "@/commons/constants/MobileUrl";

import OtherUserSafetyConfirmsTemplatesService, { OtherUserSafetyConfirmsTemplatesGetModel } from "@/commons/services/OtherUserSafetyConfirmsTemplates";
import SendMessageGroupsService, { GroupType, SendMessageGroupsGetModel } from "@/commons/services/SendMessageGroups";
import OtherUserSafetyConfirmMastersService, {
  OtherUserSafetyConfirmMastersGetModel,
  OtherUserSafetyConfirmMasterRadioInputOption
} from "@/commons/services/OtherUserSafetyConfirmMasters";
import OtherUserGroupsService from "@/commons/services/OtherUserGroups";
import OtherUserGroup from "@/commons/models/OtherUserGroup";
import OtherUserGroupMembersService from "@/commons/services/OtherUserGroupMembers";
import { PlanType } from "@/commons/services/Plans";

import Loading from "@/mobile/basics/Loading.vue";
import ValidationAlert from "@/mobile/basics/ValidationAlertByValidator.vue";
import ProcessIndicator, { ProcessIndicatorModel } from "@/mobile/basics/ProcessIndicator.vue";
import UseSwitch from "@/mobile/basics/UseSwitch.vue";
import SafetyConfirmOtherUserQuestions, { UseId } from "@/mobile/components/SafetyConfirmOtherUserQuestions.vue";
import SafetyConfirmTargetMessageGroupModal, {
  SafetyConfirmTargetMessageGroupModalModel,
  SafetyConfirmTargetMessageGroupModel,
  SafetyConfirmTargetOtherUserGroupModel,
  TargetType
} from "@/mobile/components/modals/SafetyConfirmTargetMessageGroupModal.vue";
import OkCancelModal from "@/mobile/basics/OkCancelModal.vue";

/**
 * ### 安否確認 事前設定(施設関係者) 入力コンポーネント
 */
@Component({
  components: {
    Loading,
    OkCancelModal,
    ValidationAlert,
    ProcessIndicator,
    UseSwitch,
    SafetyConfirmOtherUserQuestions,
    SafetyConfirmTargetMessageGroupModal
  },
  $_veeValidate: { validator: "new" }
})
export default class OtherUserTemplateInput extends Vue {
  /** 安否確認(施設関係者)テンプレートサービス */
  @Inject("OtherUserSafetyConfirmsTemplatesService") otherUserSafetyConfirmsTemplatesService!: OtherUserSafetyConfirmsTemplatesService;
  /** 安否確認(施設関係者)で送信する質問内容のマスタサービス */
  @Inject("OtherUserSafetyConfirmMastersService") otherUserSafetyConfirmMastersService!: OtherUserSafetyConfirmMastersService;
  /** 宛先グループサービス */
  @Inject("SendMessageGroupsService") sendMessageGroupsService!: SendMessageGroupsService;
  /** 施設関係者グループサービス */
  @Inject("OtherUserGroupsService") otherUserGroupsService!: OtherUserGroupsService;
  /** 施設関係者グループメンバー取得サービス */
  @Inject("OtherUserGroupMembersService") otherUserGroupMembersService!: OtherUserGroupMembersService;

  /** ### 安否確認事前設定(施設関係者)画面での入力情報 */
  @Getter("mobileSafetyConfirmOtherUserTemplateState") state!: MobileSafetyConfirmOtherUserTemplateState;

  /** インジケータ */
  indicator: Array<ProcessIndicatorModel> = [
    { text: "入力", current: true },
    { text: "確認", current: false },
    { text: "完了", current: false }
  ];

  /** 読み込み中モーダル表示可否 */
  isBusy: boolean = false;
  /** 読み込み中モーダルメッセージ */
  loadingMessage: string = "読込み中";

  /** 安否確認(施設関係者)で送信する質問内容のマスタ */
  masters: Array<OtherUserSafetyConfirmMastersGetModel> = [];
  /** 安否確認(施設関係者)テンプレート */
  template: OtherUserSafetyConfirmsTemplatesGetModel | null = null;

  /** グループ */
  groupTargets: Array<SafetyConfirmTargetMessageGroupModalModel> = [];
  /** 選択しているグループ種別 */
  selectedGroupType: TargetType = TargetType.NONE;
  /** 冒頭文 */
  openingSentence: string = "";
  /** 質問リスト */
  confirms: Array<UseId> = [];
  /** 安否確認テンプレート(施設関係者)の更新日時 */
  updated: number | null = null;

  /** 「グループ」宛先選択モーダル表示可否 */
  showGroupTargetModal: boolean = false;

  /** 宛先削除モーダル表示可否 */
  showSendTargetDeleteModal: boolean = false;
  /** 宛先削除モーダルメッセージ */
  sendTargetDeleteMessage: string = "";
  /** 宛先削除対象の一時保存 */
  sendTargetDeleteTarget: any = null;

  /** エラーメッセージ */
  errorMessage: string = "";

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    this.createView();
    return;
  }

  /**
   * ### 画面表示情報を作成する
   *
   * - 更新対象の安否確認(施設関係者)テンプレートを取得する
   * - テンプレートの使用する質問のMAPを作る
   * - グループ選択肢を作成する
   * - 安否確認(施設関係者)で送信する質問内容のマスタを取得する
   * - 質問のMAP、質問内容のマスタから質問の使用する/しないの入力欄を作成する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;

    // 宛先の作成
    this.groupTargets = await this.createGroupTargets();

    const template: OtherUserSafetyConfirmsTemplatesGetModel = await this.getTemplate();
    const useIdMap: Map<number, boolean> = this.createUseIdMap(template);
    this.template = template;
    // 宛先情報の復元は分岐が必要
    // v3.33.0以前のバージョンは宛先グループのgroupId（単体）を保持している
    // v3.34.0以降のバージョンは宛先情報（複数）を保持している
    // 宛先情報（複数）が存在する場合はv3.34.0以降のデータと判断し、存在せずgroupIdが存在する場合はv3.33.0以前のデータと判断する
    if (template.targets) {
      this.restoreTargetsByLatest(template.targets, this.groupTargets);
    } else if (template.groupId) {
      this.restoreTargetsByOld(template, this.groupTargets);
    }
    this.openingSentence = template.openingSentence;
    this.updated = template.updated;

    const masters: Array<OtherUserSafetyConfirmMastersGetModel> = await this.getOtherUserSafetyConfirmMasters();
    this.masters = masters;

    const confirms: Array<UseId> = this.createConfirms(masters, useIdMap);
    this.confirms = confirms;

    this.restoreInputValues();

    this.isBusy = false;

    return;
  }

  /**
   * ### 安否確認(施設関係者)テンプレートを取得する
   *
   * - 現時点ではテンプレートは1件のみなのでid指定は1固定
   */
  public async getTemplate(): Promise<OtherUserSafetyConfirmsTemplatesGetModel> {
    const response: OtherUserSafetyConfirmsTemplatesGetModel = await this.otherUserSafetyConfirmsTemplatesService.get(userStore, 1);
    return response;
  }

  /**
   * ### テンプレートの使用する質問のMAPを作る
   *
   * - テンプレートの使用するIDリストに格納されている質問のみ使用する
   */
  public createUseIdMap(template: OtherUserSafetyConfirmsTemplatesGetModel): Map<number, boolean> {
    let map: Map<number, boolean> = new Map<number, boolean>();

    for (const useId of template.useIds) {
      map.set(useId, true);
    }

    return map;
  }

  /**
   * ### 宛先を復元する（v3.33.0以前）
   *
   * - 宛先グループIDで検索して一致するものを宛先対象とする
   * - v3.33.0以前は宛先グループ（施設関係者）しか宛先に設定できなかったの検索対象はotherUserGroupMemberGroupsのみ
   */
  private restoreTargetsByOld(template: OtherUserSafetyConfirmsTemplatesGetModel, groupTargets: Array<SafetyConfirmTargetMessageGroupModalModel>): void {
    for (const groupTarget of groupTargets) {
      switch (groupTarget.type) {
        case TargetType.SEND_MESSAGE_GROUP_FOR_OTHER_USER: {
          for (const target of groupTarget.targets) {
            const _target = target as SafetyConfirmTargetMessageGroupModel;
            if (_target.id == template.groupId) {
              target.isTarget = true;
              this.selectedGroupType = groupTarget.type;
            }
          }
          break;
        }
      }
    }
    return;
  }

  /**
   * ### 宛先を復元する（v3.34.0以降）
   *
   * - ソートキーリストで検索して一致するものを宛先対象とする
   * - 宛先グループ（施設関係者）、施設関係者グループのみが対象となる
   */
  private restoreTargetsByLatest(targets: string[], groupTargets: Array<SafetyConfirmTargetMessageGroupModalModel>): void {
    // 検索用にmapにする
    const map: Map<string, string> = new Map<string, string>();
    for (const target of targets) {
      map.set(target, target);
    }

    for (const groupTarget of groupTargets) {
      switch (groupTarget.type) {
        case TargetType.SEND_MESSAGE_GROUP_FOR_OTHER_USER: {
          for (const target of groupTarget.targets) {
            const _target = target as SafetyConfirmTargetMessageGroupModel;
            if (map.has(String(_target.id))) {
              target.isTarget = true;
              this.selectedGroupType = groupTarget.type;
            }
          }
          break;
        }
        case TargetType.OTHER_USER_GROUP: {
          for (const target of groupTarget.targets) {
            const _target = target as SafetyConfirmTargetOtherUserGroupModel;
            if (map.has(String(_target.otherUserGroupsSortKey))) {
              target.isTarget = true;
              this.selectedGroupType = groupTarget.type;
            }
          }
          break;
        }
      }
    }
    return;
  }

  /**
   * ### 「宛先グループ」の宛先対象を作成する
   *
   * - フリー、ライトプランは施設関係者グループを非表示にする
   */
  public async createGroupTargets(): Promise<Array<SafetyConfirmTargetMessageGroupModalModel>> {
    const safetyConfirmTargetMessageGroupModalModel: Array<SafetyConfirmTargetMessageGroupModalModel> = [];

    // 宛先グループの選択肢作成
    if (PlanType.BASIC <= userStore.facility.planType) {
      const messageGroups: Array<SendMessageGroupsGetModel> = await this.getMessageGroups();
      const targetGroupForOtherUser: Array<SafetyConfirmTargetMessageGroupModel> = [];
      for (const group of messageGroups) {
        if (group.type == GroupType.OTHER_USER) {
          targetGroupForOtherUser.push({
            ...group,
            isTarget: false,
            show: true,
            memberCount: group.members.length
          });
        }
      }
      // 選択肢をモーダル表示用の情報に整形する
      if (PlanType.BASIC <= userStore.facility.planType) {
        safetyConfirmTargetMessageGroupModalModel.push({
          name: "宛先グループ（施設関係者）",
          targets: targetGroupForOtherUser,
          show: true,
          type: TargetType.SEND_MESSAGE_GROUP_FOR_OTHER_USER
        });
      }
    }

    // 施設関係者グループの選択肢作成
    if (PlanType.LIGHT <= userStore.facility.planType) {
      // 全施設関係者メンバーを取得して施設関係者グループにひもづくメンバー人数を集計する
      const otherUserGroupMembersInFacility: Array<OtherUserGroup> = await this.getOtherUserGroupMembersInFacility();
      const otherUserGroupMemberCountMap = new Map<number, number>();
      for (const member of otherUserGroupMembersInFacility) {
        let memberCount: number | undefined = otherUserGroupMemberCountMap.get(member.id);
        if (memberCount) {
          memberCount++;
          otherUserGroupMemberCountMap.set(member.id, memberCount);
        } else {
          otherUserGroupMemberCountMap.set(member.id, 1);
        }
      }
      // メンバーが1人以上所属する施設関係者グループを選択肢の対象にする
      const otherUserGroups: Array<OtherUserGroup> = await this.getOtherUserGroups();
      const targetOtherUserGroups: Array<SafetyConfirmTargetOtherUserGroupModel> = [];
      for (const group of otherUserGroups) {
        const memberCount: number | undefined = otherUserGroupMemberCountMap.get(group.id);
        targetOtherUserGroups.push({
          ...group,
          isTarget: false,
          show: true,
          memberCount: memberCount ? memberCount : 0
        });
      }
      // 選択肢をモーダル表示用の情報に整形する
      safetyConfirmTargetMessageGroupModalModel.push({
        name: "施設関係者グループ",
        targets: targetOtherUserGroups,
        show: true,
        type: TargetType.OTHER_USER_GROUP
      });
    }

    return safetyConfirmTargetMessageGroupModalModel;
  }

  /**
   * ### 宛先グループを取得する
   */
  public async getMessageGroups(): Promise<Array<SendMessageGroupsGetModel>> {
    const groups: Array<SendMessageGroupsGetModel> = await this.sendMessageGroupsService.gets(userStore);
    return groups;
  }

  /**
   * ### 施設にひもづく施設関係者グループメンバーを取得する
   */
  public async getOtherUserGroupMembersInFacility(): Promise<Array<OtherUserGroup>> {
    const response: Array<OtherUserGroup> = await this.otherUserGroupMembersService.get(userStore);
    return response;
  }

  /**
   * ### 施設関係者グループを取得する
   */
  public async getOtherUserGroups(): Promise<Array<OtherUserGroup>> {
    const response: Array<OtherUserGroup> = await this.otherUserGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### 安否確認(施設関係者)で送信する質問内容のマスタを取得する
   */
  public async getOtherUserSafetyConfirmMasters(): Promise<Array<OtherUserSafetyConfirmMastersGetModel>> {
    const response: Array<OtherUserSafetyConfirmMastersGetModel> = await this.otherUserSafetyConfirmMastersService.get(userStore);
    return response;
  }

  /**
   * ### 入力させる使用する質問リストを作成する
   *
   * - 入力種別がラジオボタンの質問は選択肢ラベルを作成する
   * - テンプレート情報から質問を使用するかしないかを設定する
   */
  public createConfirms(masters: Array<OtherUserSafetyConfirmMastersGetModel>, useIdMap: Map<number, boolean>): Array<UseId> {
    let confirms: Array<UseId> = [];

    for (const master of masters) {
      let choicesLabel: string = "";
      if (master.inputType == 0) {
        const options: OtherUserSafetyConfirmMasterRadioInputOption = master.inputOptions as OtherUserSafetyConfirmMasterRadioInputOption;
        for (const choice of options.choices) {
          choicesLabel += "／" + choice.label;
        }
        // 先頭に「／」が入っているので、消す
        choicesLabel = choicesLabel.slice(1);
      }

      const use: boolean | undefined = useIdMap.get(master.id);
      const useId: UseId = {
        ...master,
        choicesLabel: choicesLabel,
        use: use == true ? true : false
      };
      confirms.push(useId);
    }

    return confirms;
  }

  /**
   * ### 入力情報の復元
   *
   * - storeに入力情報が保持されていれば復元する
   * - 復元後はstoreの内容をリセットする
   */
  public restoreInputValues(): void {
    if (this.state.exist == true) {
      this.groupTargets = this.state.groupTargets;
      this.openingSentence = this.state.openingSentence;
      this.confirms = this.state.confirms;
      mobileSafetyConfirmOtherUserTemplateStore.resetMobileSafetyConfirmOtherUserTemplateState();
    }

    return;
  }

  /**
   * ### 宛先の押下イベント
   *
   * - 削除対象の宛先を保持する
   * - 削除用のメッセージを作成する
   * - 宛先削除モーダルを表示する
   */
  public onSendTarget(target: any): void {
    const deleteTarget: SafetyConfirmTargetMessageGroupModel = target as SafetyConfirmTargetMessageGroupModel;
    const message: string = `${deleteTarget.name}を宛先から削除しますか？`;
    this.sendTargetDeleteTarget = deleteTarget;
    this.sendTargetDeleteMessage = message;

    this.showSendTargetDeleteModal = true;

    return;
  }

  /**
   * ### 宛先選択モーダルを表示する
   *
   * - モバイルなので宛先種別に応じて宛先候補を取得する
   */
  public async showTargetSelectModal(): Promise<void> {
    this.isBusy = true;
    if (this.groupTargets.length <= 0) {
      this.groupTargets = await this.createGroupTargets();
    }
    this.showGroupTargetModal = true;
    this.isBusy = false;
    return;
  }

  /**
   * ### 宛先対象から削除する
   */
  public onSendTargetDeleteOk(): void {
    this.sendTargetDeleteTarget.isTarget = false;
    return;
  }

  /**
   * ### プレビュー画面へ遷移する
   *
   * - バリデーションチェック
   * - バリデーションチェックでエラーが無ければ以下を実施
   *   - ストアに入力情報を格納
   *   - 確認画面へ遷移する
   */
  public async gotoConfirm(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();

    if (validationResult) {
      this.showValidationAlert = false;
      this.isBusy = true;

      const state: MobileSafetyConfirmOtherUserTemplateState = {
        groupTargets: this.groupTargets,
        openingSentence: this.openingSentence,
        confirms: this.confirms,
        updated: this.updated,
        exist: true
      };
      mobileSafetyConfirmOtherUserTemplateStore.updateMobileSafetyConfirmOtherUserTemplateState({ state: state });

      this.isBusy = false;

      this.$router.push(MobileUrl.SafetyConfirmOtherUserTempleteConfirm);
    } else {
      this.showValidationAlert = true;
    }

    return;
  }

  /**
   * ### フッターを表示する
   */
  public showFooter(): void {
    this.$emit("updateShowFooter", true);
    return;
  }

  /**
   * ### フッターを非表示にする
   */
  public hideFooter(): void {
    this.$emit("updateShowFooter", false);
    return;
  }
}
</script>

<style lang="scss" scopeed>
@import "../../../styles/mobile/_colors.scss";

#other-user-template-input {
  .target-add-btn {
    min-width: 6rem;
  }
  .target-names {
    .target-name {
      color: $font-key;
      background-color: $bg-default;
      min-height: 2.3rem !important;
      padding: 2px 10px !important;
      margin: 0 0.5rem 0.5rem 0;
      border-radius: 0.25rem;
      border: 1px solid $border-default;
      word-break: break-all;
    }
  }
}
</style>
